// กริดภูมิศาสตร์ 1° ทั้งโลก (docs/PLAN.md ข้อ 4.2) สร้างโดย scripts/build-geo.ts เก็บไว้ที่ public/geo/grid.bin.gz
// ใช้ตอบว่าช่องไหนเป็นบกหรือทะเล อยู่ประเทศอะไร ทะเลอะไร สูงหรือลึกเท่าไร และเป็นพื้นที่ประเภทไหน

import { CELL_COUNT, neighbors } from "../engine/grid";
import { LAND_BIOMES, type LandBiomeId } from "./biomes";
import { COUNTRY_ANCHORS, COUNTRY_CODES, SEA_NAMES } from "./generated/regions";
import type { Province } from "./provinces";
import { SEA_NAME_TH } from "./sea-names";

export const FULL_LAND = 16; // จุดตัวอย่างต่อช่องทั้งหมด (4 × 4)

// ตำแหน่งเริ่มของแต่ละชั้นในไฟล์ (byte)
export const LAYER = {
  land: 0, // uint8 จำนวนจุดตัวอย่างที่เป็นแผ่นดิน 0–16
  country: CELL_COUNT, // uint8 เลขประเทศใน COUNTRY_CODES (0 = ไม่มี)
  sea: CELL_COUNT * 2, // uint8 เลขทะเลใน SEA_NAMES (0 = ไม่มีน้ำ)
  elevation: CELL_COUNT * 3, // int16 little-endian ความสูงเฉลี่ยของส่วนที่เป็นแผ่นดิน (เมตร)
  depth: CELL_COUNT * 5, // uint16 little-endian ความลึกเฉลี่ยของส่วนที่เป็นทะเล (เมตร)
  biome: CELL_COUNT * 7, // uint8 biome ส่วนใหญ่ของแผ่นดินในช่อง เลขใน LAND_BIOMES (0 = ไม่มี)
  flags: CELL_COUNT * 8, // uint8 บิต 0 = มีป่าชายเลนในช่อง
} as const;
export const GRID_BYTES = CELL_COUNT * 9;
export const FLAG_MANGROVE = 1;

/** เปลี่ยนเมื่อรูปแบบไฟล์เปลี่ยน ให้เบราว์เซอร์โหลดไฟล์ใหม่แทนไฟล์เก่าในแคช */
export const GRID_VERSION = 2;

export interface GeoGrid {
  land: Uint8Array;
  country: Uint8Array;
  sea: Uint8Array;
  elevation: Int16Array;
  depth: Uint16Array;
  biome: Uint8Array;
  flags: Uint8Array;
  provinces?: readonly Province[]; // จังหวัดในไทย โหลดแยกจากกริด (src/geo/provinces.ts) โหลดไม่ได้ก็บอกแค่ชื่อประเทศ
}

export function parseGrid(bytes: Uint8Array): GeoGrid {
  if (bytes.length !== GRID_BYTES) throw new Error("ไฟล์กริดภูมิศาสตร์ไม่ถูกต้อง");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const elevation = new Int16Array(CELL_COUNT);
  const depth = new Uint16Array(CELL_COUNT);
  for (let c = 0; c < CELL_COUNT; c++) {
    elevation[c] = view.getInt16(LAYER.elevation + c * 2, true);
    depth[c] = view.getUint16(LAYER.depth + c * 2, true);
  }
  return {
    land: bytes.slice(LAYER.land, LAYER.land + CELL_COUNT),
    country: bytes.slice(LAYER.country, LAYER.country + CELL_COUNT),
    sea: bytes.slice(LAYER.sea, LAYER.sea + CELL_COUNT),
    elevation,
    depth,
    biome: bytes.slice(LAYER.biome, LAYER.biome + CELL_COUNT),
    flags: bytes.slice(LAYER.flags, LAYER.flags + CELL_COUNT),
  };
}

export function landBiomeAt(grid: GeoGrid, cell: number): LandBiomeId | null {
  return LAND_BIOMES[grid.biome[cell]] ?? null;
}

export const hasMangrove = (grid: GeoGrid, cell: number) => (grid.flags[cell] & FLAG_MANGROVE) !== 0;

export const hasLand = (grid: GeoGrid, cell: number) => grid.land[cell] > 0;
export const hasSea = (grid: GeoGrid, cell: number) => grid.land[cell] < FULL_LAND;

export function countryAt(grid: GeoGrid, cell: number): string | null {
  return COUNTRY_CODES[grid.country[cell]] || null;
}

/** ประเทศของช่องนี้ หรือของช่องที่ใกล้ที่สุดในระยะ radius (เช่น ทะเลชายฝั่ง) */
export function nearestCountry(grid: GeoGrid, cell: number, radius = 2): string | null {
  const own = countryAt(grid, cell);
  if (own) return own;
  for (let r = 1; r <= radius; r++) {
    let best: { code: string; land: number } | null = null;
    for (const n of neighbors(cell, r)) {
      const code = countryAt(grid, n);
      if (code && (!best || grid.land[n] > best.land)) best = { code, land: grid.land[n] };
    }
    if (best) return best.code;
  }
  return null;
}

/** ชื่อทะเลภาษาไทยของช่องนี้ (null = ไม่มีน้ำ) */
export function seaNameAt(grid: GeoGrid, cell: number): string | null {
  const name = SEA_NAMES[grid.sea[cell]];
  return name ? SEA_NAME_TH[name] ?? null : null;
}

const countryIndex = new WeakMap<GeoGrid, Map<string, number[]>>();

/** ช่องทั้งหมดที่เป็นแผ่นดินของประเทศนี้ ประเทศเล็กที่ไม่มีช่องของตัวเองใช้ช่องของจุดวางชื่อประเทศแทน */
export function countryCells(grid: GeoGrid, code: string): number[] {
  let index = countryIndex.get(grid);
  if (!index) {
    index = new Map();
    for (let c = 0; c < CELL_COUNT; c++) {
      const cc = countryAt(grid, c);
      if (!cc) continue;
      if (!index.has(cc)) index.set(cc, []);
      index.get(cc)!.push(c);
    }
    countryIndex.set(grid, index);
  }
  const cells = index.get(code);
  if (cells?.length) return cells;
  const anchor = COUNTRY_ANCHORS[code];
  return anchor === undefined ? [] : [anchor];
}
